/**
 * CaseTimeline component for the Indigo design system.
 *
 * Vertical chronological list of "key events" that make up a case (car
 * accident, diagnostic studies, injections, depositions, upcoming
 * evaluation, …). Each entry pairs a left-aligned date with a card that
 * carries a small tinted icon tile, a title, an optional link-out affordance,
 * an optional "New" tag, and a stack of description / bulleted text.
 *
 * Entries can be grouped into periods (e.g. "Pre-injury", "Claimed injury",
 * "Since last appointment") by setting `periodLabel` on the first entry of a
 * period — a dashed divider with a centered chip is rendered before it.
 *
 * Cards are read-only unless an entry sets `onClick` (whole card clickable);
 * `footer` holds block content such as a tag row. Far lighter than the
 * medical-record `Timeline` component (which is virtualised and carries
 * filters / diff mode). Use this one for small, static timelines.
 *
 * @example
 * ```tsx
 * <CaseTimeline
 *   entries={[
 *     {
 *       id: "mri-2019",
 *       periodLabel: "Pre-injury",
 *       date: "03/01/2019",
 *       iconName: "file",
 *       iconColor: "olive",
 *       title: "Diagnostic studies: MRI",
 *       onOpen: openSource,
 *       bullets: [{ value: "Multilevel degenerative disc disease…" }],
 *     },
 *     {
 *       id: "injury",
 *       periodLabel: "Claimed injury",
 *       date: "08/01/2024",
 *       iconName: "car",
 *       iconColor: "rose",
 *       title: "Date of injury: car accident on the way to work",
 *       description: "Video shows the patient was rear-ended on the highway.",
 *     },
 *   ]}
 * />
 * ```
 */
import { FC, KeyboardEvent, ReactNode } from "react";
import { Card } from "../Card/Card";
import { Icon, type IconProps } from "../Icons";
import { Tag } from "../Tag/Tag";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./CaseTimeline.css";

/** Semantic tint applied to an entry's icon tile (soft background + icon color). */
export type CaseTimelineIconColor =
  | "olive"
  | "amber"
  | "sky"
  | "rose"
  | "neutral"
  | "subtle";

export interface CaseTimelineBullet {
  /**
   * Optional label rendered in bold before the value. When omitted, only
   * the value is shown so the bullet reads as a plain line of text.
   */
  label?: string;
  /** Bullet content. Falsy values cause the bullet to be skipped. */
  value: ReactNode;
}

export interface CaseTimelineEntry {
  /** Stable id used for React keys. */
  id: string;
  /** Pre-formatted date string shown to the left of the card (e.g. `08/01/2024`). */
  date: string;
  /** Entry title. */
  title: string;
  /** Icon shown in the tinted tile that leads the card header. */
  iconName: IconProps["name"];
  /** Tint for the icon tile. @default "subtle" */
  iconColor?: CaseTimelineIconColor;
  /** Renders a "New" tag next to the title. */
  isNew?: boolean;
  /** When set, renders a link-out affordance in the header that calls this handler. */
  onOpen?: () => void;
  /** Optional description paragraph shown below the title. */
  description?: ReactNode;
  /** Optional bulleted list rendered below the description. */
  bullets?: CaseTimelineBullet[];
  /**
   * Optional block content (e.g. a row of tags) rendered last in the card.
   * `description` sits inside a paragraph, so block elements go here.
   */
  footer?: ReactNode;
  /**
   * Makes the whole card clickable (e.g. to open a detail panel). Controls
   * inside the card should stop propagation of their own clicks.
   */
  onClick?: () => void;
  /**
   * When set, a dashed period divider with a centered chip is rendered
   * immediately before this entry (marks the start of a new period).
   */
  periodLabel?: string;
}

export interface CaseTimelineProps {
  /** Section title rendered above the entries. Omit to render without a wrapping Card. */
  title?: string;
  /** Entries to display, top-to-bottom. */
  entries: CaseTimelineEntry[];
  /** Text shown when `entries` is empty. */
  emptyMessage?: string;
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Data test id for testing. */
  "data-testid"?: string;
}

const isNonEmpty = (value: ReactNode): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

export const CaseTimeline: FC<CaseTimelineProps> = ({
  title,
  entries,
  emptyMessage = "No events yet.",
  className,
  "data-testid": dataTestId,
}) => {
  const body =
    entries.length === 0 ? (
      <Body size="md" className="indigo-case-timeline__empty">
        {emptyMessage}
      </Body>
    ) : (
      <ol
        className="indigo-case-timeline indigo-case-timeline__list"
        data-testid={dataTestId}
      >
        {entries.map((entry) => {
          const visibleBullets =
            entry.bullets?.filter((bullet) => isNonEmpty(bullet.value)) ?? [];

          return (
            <li
              className="indigo-case-timeline__item"
              key={entry.id}
              // Lets a page scroll to / highlight a specific entry.
              data-entry-id={entry.id}
            >
              {isNonEmpty(entry.periodLabel) && (
                <div
                  className="indigo-case-timeline__divider"
                  aria-hidden="true"
                >
                  <span className="indigo-case-timeline__period">
                    {entry.periodLabel}
                  </span>
                </div>
              )}
              <div className="indigo-case-timeline__entry">
                <Heading level={3} className="indigo-case-timeline__date">
                  {entry.date}
                </Heading>
                <div
                  className={
                    entry.onClick
                      ? "indigo-case-timeline__card indigo-case-timeline__card--clickable"
                      : "indigo-case-timeline__card"
                  }
                  {...(entry.onClick && {
                    role: "button",
                    tabIndex: 0,
                    onClick: entry.onClick,
                    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
                      // Keys on a control inside the card belong to it.
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        entry.onClick?.();
                      }
                    },
                  })}
                >
                  <div className="indigo-case-timeline__header">
                    <span
                      className={`indigo-case-timeline__icon indigo-case-timeline__icon--${
                        entry.iconColor ?? "subtle"
                      }`}
                      aria-hidden="true"
                    >
                      <Icon name={entry.iconName} size="xs" />
                    </span>
                    <Heading level={3} className="indigo-case-timeline__title">
                      {entry.title}
                    </Heading>
                    {entry.onOpen && (
                      <button
                        type="button"
                        className="indigo-case-timeline__link"
                        onClick={(e) => {
                          // Don't also fire the card's onClick.
                          e.stopPropagation();
                          entry.onOpen?.();
                        }}
                        aria-label={`Open source for ${entry.title}`}
                      >
                        <Icon name="link-out" size="xs" />
                      </button>
                    )}
                    {entry.isNew && (
                      <Tag
                        label="New"
                        size="small"
                        shape="square"
                        className="indigo-case-timeline__new"
                      />
                    )}
                  </div>
                  {isNonEmpty(entry.description) && (
                    <Body
                      size="md"
                      className="indigo-case-timeline__description"
                    >
                      {entry.description}
                    </Body>
                  )}
                  {visibleBullets.length > 0 && (
                    <ul className="indigo-case-timeline__bullets">
                      {visibleBullets.map((bullet, index) => (
                        <li
                          className="indigo-case-timeline__bullet"
                          // Labels repeat legitimately — a card can carry several
                          // bullets for the same modality (e.g. two chest X-ray reads on
                          // one date), so the label alone is not a unique key.
                          key={`${bullet.label ?? ""}-${index}`}
                        >
                          <Body
                            size="md"
                            className="indigo-case-timeline__bullet-text"
                          >
                            {bullet.label ? (
                              <>
                                <span className="indigo-case-timeline__bullet-label">
                                  {bullet.label}:
                                </span>{" "}
                                <span>{bullet.value}</span>
                              </>
                            ) : (
                              bullet.value
                            )}
                          </Body>
                        </li>
                      ))}
                    </ul>
                  )}
                  {isNonEmpty(entry.footer) && (
                    <div className="indigo-case-timeline__footer">
                      {entry.footer}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    );

  if (!title) {
    return <div className={className}>{body}</div>;
  }

  return (
    <Card title={title} titleOutside className={className} size="medium">
      {body}
    </Card>
  );
};

CaseTimeline.displayName = "CaseTimeline";

export default CaseTimeline;
